import { ref, unref, watch } from 'vue'

import store from '@/store'
import { config } from '@/config.js'

const defaultFilters = () => ({
    page: 1,
    per_page: 10
})

const defaultMeta = () => ({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
})

export function useAdminCesionHistorial(abonadoIdRef) {
    const historial = ref([])
    const loading = ref(false)
    const error = ref(null)
    const filters = ref(defaultFilters())
    const meta = ref(defaultMeta())

    function extractServerMessage(err, fallback) {
        return err?.response?.data?.message
            || err?.response?.data?.error
            || fallback
    }

    function normalizeMeta(payloadMeta = {}, payload = {}) {
        return {
            total: payloadMeta.total ?? payload.total ?? 0,
            current_page: payloadMeta.current_page ?? filters.value.page,
            per_page: payloadMeta.per_page ?? filters.value.per_page,
            last_page: payloadMeta.last_page ?? 1
        }
    }

    async function loadHistorial() {
        const abonadoId = unref(abonadoIdRef)

        if (!abonadoId) {
            historial.value = []
            meta.value = defaultMeta()
            return
        }

        loading.value = true
        error.value = null

        try {
            const response = await store.dispatch(
                config.getVuexAlias('back.abonados.cesion-historial'),
                {
                    abonado_id: abonadoId,
                    filters: {
                        page: filters.value.page,
                        per_page: filters.value.per_page
                    }
                }
            )

            const payload = response?.data || {}
            historial.value = Array.isArray(payload.data) ? payload.data : []
            meta.value = normalizeMeta(payload.meta, payload)
        } catch (err) {
            error.value = extractServerMessage(err, 'Error al cargar el historial de transferencias')

            if (process.env.NODE_ENV !== 'production') {
                console.error('AdminCesionHistorial error:', err)
            }
        } finally {
            loading.value = false
        }
    }

    function updateFilters(nextFilters = {}) {
        const nextPage = Number.parseInt(nextFilters.page ?? filters.value.page, 10) || 1
        const nextPerPage = Number.parseInt(nextFilters.per_page ?? filters.value.per_page, 10) || filters.value.per_page
        const perPageChanged = nextPerPage !== filters.value.per_page

        filters.value = {
            ...filters.value,
            ...nextFilters,
            page: perPageChanged ? 1 : nextPage,
            per_page: nextPerPage
        }
    }

    watch(
        () => unref(abonadoIdRef),
        (abonadoId, previousAbonadoId) => {
            if (!abonadoId) {
                historial.value = []
                error.value = null
                filters.value = defaultFilters()
                meta.value = defaultMeta()
                return
            }

            if (abonadoId !== previousAbonadoId) {
                filters.value = defaultFilters()
                meta.value = defaultMeta()
            }
        },
        { immediate: true }
    )

    watch(
        () => [unref(abonadoIdRef), filters.value.page, filters.value.per_page],
        ([abonadoId]) => {
            if (!abonadoId) {
                return
            }

            loadHistorial()
        },
        { immediate: true }
    )

    return {
        historial,
        loading,
        error,
        filters,
        meta,
        loadHistorial,
        updateFilters
    }
}
